import { Button, Card, Flex, Grid, Link, Separator, Text } from "@radix-ui/themes";
import { Link as InertiaLink } from "@inertiajs/react";
import { CalendarClock, FileEdit, MapPin } from "lucide-react";
import { TrackManagerTrack } from "@/types/shared";

interface Props {
  track: TrackManagerTrack;
  /** Rendered inside a mobile card that already shows this record's identity, so
   *  drop whatever the card is already saying. Each panel keeps what the card can't. */
  compact?: boolean;
}

interface CountCardProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  color?: string;
}

function CountCard({ icon, label, count, color }: CountCardProps) {
  return (
    <Card>
      <Flex direction="column" gap="1">
        <Flex align="center" gap="1" style={{ color: color ?? "inherit" }}>
          {icon}
          <Text size="1" color="gray">
            {label}
          </Text>
        </Flex>
        <Text size="7" weight="bold" style={{ color: color ?? "inherit" }}>
          {count}
        </Text>
      </Flex>
    </Card>
  );
}

export default function TrackDetailPanel({ track, compact = false }: Props) {
  const publicPath = `/tracks/${track.slug}`;

  return (
    <Flex direction="column" gap="4">
      {/* The card already shows the name and the city, and "View Track" below is
          the public link — so in a card there's nothing here worth repeating. */}
      {!compact && (
        <>
          <Flex direction="column" gap="2">
            <Link href={publicPath} size="5" weight="bold">
              {track.name}
            </Link>
            <Flex align="center" gap="1">
              <MapPin size={14} />
              <Text size="2" color="gray">
                {track.city}, {track.state}
              </Text>
            </Flex>
          </Flex>

          <Separator size="4" />
        </>
      )}

      {/* Event counts */}
      <Flex direction="column" gap="2">
        <Text size="2" weight="medium">
          Events
        </Text>
        <Grid columns="2" gap="2">
          <CountCard
            icon={<CalendarClock size={14} />}
            label="Scheduled"
            count={track.scheduled_count}
            color="var(--green-11)"
          />
          <CountCard
            icon={<FileEdit size={14} />}
            label="Draft"
            count={track.draft_count}
          />
        </Grid>
      </Flex>

      <Separator size="4" />
      <Grid columns="2" gap="2">
        <Button asChild variant="soft" color="gray" size="2">
          <a href={publicPath}>View Track</a>
        </Button>
        <Button asChild variant="soft" size="2">
          <InertiaLink href={track.edit_path}>Edit Venue Info</InertiaLink>
        </Button>
      </Grid>
    </Flex>
  );
}
